import { useCallback, useEffect, useMemo } from 'react';
import { ShopCategoriesGrid } from '~/components/ui/Shop';
import {
	ShopCategory,
	useShopCategoryControls,
} from '~/components/ui/ShopCategory';
import { rage, useObservable } from '~/rage';
import { BarberShopSection } from '~/shared';
import {
	setActiveCategory,
	setActiveStage,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { categories } from './CategoryTab.model';

const CategoryTab = () => {
	const dispatch = useLayoutDispatch();
	const activeCategory = useLayoutSelector(
		(state) => state.barberShop.activeCategory,
	);
	const sections = useObservable(rage.barberShop.getSections());
	const items = useObservable(rage.barberShop.getItems());
	const playerGender = useObservable(rage.player.getPlayerGender());
	const overlays = useObservable(
		rage.barberShop.getOverlayConfigsByGender(playerGender),
	);

	const filteredCategories = useMemo(() => {
		return categories
			.filter((category) => sections.some((s) => s.id === category.type))
			.filter(
				(category) =>
					items?.some((item) => item.shopSection === category.type) ?? false,
			)
			.map((category) => ({
				...category,
				name:
					sections.find((section) => section.id === category.type)?.name ??
					category.name,
			}));
	}, [sections, items]);

	const categoryUnseenMap = useMemo(() => {
		const map: Partial<Record<BarberShopSection, boolean>> = {};

		for (const category of filteredCategories) {
			const categoryItems = items?.filter(
				(item) =>
					item.shopSection === category.type &&
					overlays?.some(
						(ov) =>
							ov.section === item.overlaySection && ov.index === item.index,
					),
			);

			if (!categoryItems || categoryItems.length === 0) {
				continue;
			}

			for (const item of categoryItems) {
				const seenTrackerKey = `${category.type}.${item.overlaySection}.${item.index}`;
				if (rage.barberShop.isUnseen(seenTrackerKey)) {
					map[category.type] = true;
					break;
				}
			}
		}

		return map;
	}, [filteredCategories, items, overlays]);

	const handleSelect = useCallback(
		(index: number) => {
			if (filteredCategories?.[index] === undefined) {
				return;
			}

			dispatch(setActiveStage(StageTypes.List));
			dispatch(setActiveCategory(filteredCategories[index].type));
		},
		[filteredCategories],
	);

	const { setMaxCategoryIndex, activeCategoryIndex, setActiveCategoryIndex } =
		useShopCategoryControls(
			activeCategory
				? filteredCategories.map((item) => item.type).indexOf(activeCategory)
				: null,
			handleSelect,
		);

	useEffect(() => {
		if (activeCategoryIndex === null) {
			dispatch(setActiveCategory(null));
			return;
		}

		dispatch(setActiveCategory(filteredCategories[activeCategoryIndex].type));
	}, [activeCategoryIndex, filteredCategories]);

	useEffect(() => {
		setMaxCategoryIndex(filteredCategories.length);
	}, [filteredCategories]);

	return (
		<ShopCategoriesGrid style={{ marginTop: 30 }}>
			{filteredCategories.map((item, index) => {
				const seenTrackerKey = `${item.type}`;

				return (
					<ShopCategory
						name={item.name}
						icon={item.image}
						onClick={() => handleSelect(index)}
						isActive={activeCategory === item.type}
						isNew={
							categoryUnseenMap[item.type] ??
							rage.barberShop.isUnseen(seenTrackerKey)
						}
						key={index}
						onMouseEnter={() => setActiveCategoryIndex(index)}
						onMouseLeave={() => setActiveCategoryIndex(null)}
					/>
				);
			})}
		</ShopCategoriesGrid>
	);
};

export { CategoryTab };
